/**
 * Trait activation checker - determines if traits are active based on game state
 */

import type { Unit } from '../types/unit';
import { MovementType } from '../types/unit';
import { Motion } from '../types/motion';
import { SHOCK_TRAIT_RANGE_M } from './shock-trait-constants';

export interface TraitActivationContext {
  sourceUnit: Unit;
  targetUnit: Unit;
  distance: number;
  sourceMotion: Motion;
  targetMotion: Motion;
  sourceTraitOverrides?: Map<string, boolean | undefined>;
  targetTraitOverrides?: Map<string, boolean | undefined>;
}

export interface TraitActivationResult {
  isActive: boolean;
  activationType: 'auto' | 'manual' | 'unknown';
  activationReason?: string;
}

/**
 * Check if a trait is active based on activation conditions and manual overrides
 */
export function isTraitActive(
  traitName: string,
  context: TraitActivationContext,
  isSourceTrait: boolean
): TraitActivationResult {
  const unit = isSourceTrait ? context.sourceUnit : context.targetUnit;
  const overrides = isSourceTrait ? context.sourceTraitOverrides : context.targetTraitOverrides;
  
  // Check if unit has the trait
  const hasTrait = unit.specialities?.includes(traitName);
  if (!hasTrait) {
    return { isActive: false, activationType: 'unknown' };
  }
  
  // Check manual override first
  const override = overrides?.get(traitName);
  if (override !== undefined) {
    return {
      isActive: override,
      activationType: 'manual',
      activationReason: override ? 'Manual override (forced on)' : 'Manual override (forced off)'
    };
  }
  
  // Check activation conditions
  let autoActive = false;
  let activationReason = '';
  
  switch (traitName) {
    case '_choc':
      autoActive =
        context.distance <= SHOCK_TRAIT_RANGE_M &&
        context.targetUnit.movementType === MovementType.LAND;
      activationReason = autoActive
        ? `Enemy within ${SHOCK_TRAIT_RANGE_M}m (distance: ${context.distance}m)`
        : `Enemy not within ${SHOCK_TRAIT_RANGE_M}m (distance: ${context.distance}m)`;
      break;
      
    case '_sniper':
      autoActive = context.sourceMotion === Motion.STATIC;
      activationReason = autoActive 
        ? 'Unit is stationary'
        : 'Unit is moving';
      break;
      
    case '_security':
      // Security trait is active when the unit with the trait is stationary
      autoActive = isSourceTrait 
        ? context.sourceMotion === Motion.STATIC
        : context.targetMotion === Motion.STATIC;
      activationReason = autoActive 
        ? 'Unit is stationary'
        : 'Unit is moving';
      break;
      
    case '_eo_dazzler':
      autoActive = context.targetMotion === Motion.STATIC;
      activationReason = autoActive 
        ? 'Target is stationary'
        : 'Target is moving';
      break;
      
    case '_electronic_warfare':
      autoActive = true; // Always active if trait present
      activationReason = 'Always active when trait present';
      break;
      
    case '_fireDirection':
      // Unknown activation - requires checking for nearby fire direction unit
      return {
        isActive: false,
        activationType: 'unknown',
        activationReason: 'Activation condition unknown (requires nearby fire direction unit)'
      };
      
    default:
      // For other traits, assume always active if present
      autoActive = true;
      activationReason = 'Always active when trait present';
      break;
  }
  
  return {
    isActive: autoActive,
    activationType: 'auto',
    activationReason
  };
}

